/**
 * The linked-tenant list + "Link tenants" control for the price book's plan
 * modal (redesign follow-up).
 *
 * ── Why it lives in `@papan/orgs`, injected as a slot ────────────────────
 *
 * `CatalogAdmin` (`@papan/billing`) renders the "Tenant-specific controls"
 * section, but listing the tenants on a plan reads `budgetApi.listTenants` — and
 * `@papan/orgs` depends on `@papan/billing`, not the other way round. So the
 * console composes this component and hands it to `CatalogAdmin` through the
 * `renderPlanTenants` slot, the same shape as the metered-pricing grid and the
 * launcher panel.
 *
 * ── What "linked" means here ─────────────────────────────────────────────
 *
 * The list is the tenants explicitly pointed at this plan (`platform_plan_override
 * = plan.id`), the SAME filter Surface 3's plan dropdown uses — deliberately NOT
 * `/plans/:id/applicable`, whose effective-plan population for the free plan is
 * ~the whole fleet. Each row shows whether the tenant has hand-set overrides
 * (`isCustom`), because "linked" and "matches the preset" are different questions.
 *
 * Linking is `planAdminApi.set(tenantId, plan.id)` — the same single-tenant plan
 * assignment the customer's own Plan tab uses. There is no bulk route; one POST
 * per pick keeps this within existing infra and audits each assignment.
 */
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from '@papan/i18n'
import { useToast } from '@papan/core-ui/ui'
import { planAdminApi } from '@papan/billing/client'
import { budgetApi, type AdminTenantRow } from '../../client/budget'

const PAGE = 25

export function PlanLinkedTenants({
  plan, canEdit,
}: {
  plan: { id: string; name: string }
  /** `customers:write` — linking a tenant to a plan writes a plan override. */
  canEdit: boolean
}) {
  const { t } = useTranslation('core-ui')
  const toast = useToast()

  const [rows, setRows] = useState<AdminTenantRow[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await budgetApi.listTenants({ planId: plan.id, limit: PAGE, offset: 0 })
      setRows(res.tenants)
      setTotal(res.total)
      setError(null)
    } catch (e: any) {
      setError(e?.message || 'Failed to load tenants on this plan')
    } finally {
      setLoading(false)
    }
  }, [plan.id])

  useEffect(() => { void load() }, [load])

  const loadMore = async () => {
    try {
      const res = await budgetApi.listTenants({ planId: plan.id, limit: PAGE, offset: rows.length })
      setRows(prev => [...prev, ...res.tenants])
      setTotal(res.total)
    } catch (e: any) {
      toast.error(e?.message || 'Failed to load more')
    }
  }

  const [linking, setLinking] = useState(false)

  return (
    <div className="border-t themed-border pt-3">
      <div className="flex items-center gap-2 mb-2">
        <span className="themed-text-secondary text-xs">
          {t('settings.platform.plan.linkedTitle', { defaultValue: 'Tenants on this plan' })}
        </span>
        <span className="themed-text-muted text-[11px]">
          {t('settings.platform.plan.linkedCount', { defaultValue: '{{count}} total', count: total })}
        </span>
      </div>

      {loading ? (
        <div className="themed-text-muted text-xs py-1">{t('common:loading')}</div>
      ) : error ? (
        <div className="text-red-400 text-xs py-1">{error}</div>
      ) : rows.length === 0 ? (
        <div className="themed-text-muted text-xs py-1">
          {t('settings.platform.plan.linkedNone', { defaultValue: 'No tenants are pointed at this plan yet.' })}
        </div>
      ) : (
        <div className="themed-bg-primary rounded border themed-border max-h-56 overflow-y-auto">
          {rows.map(r => (
            <div key={r.id} className="flex items-center gap-2 px-2.5 py-1.5 border-b themed-border last:border-b-0">
              <span className="min-w-0 flex-1">
                <span className="themed-text text-xs block truncate">{r.name}</span>
                <span className="themed-text-muted text-[10px] block truncate">{r.type}</span>
              </span>
              {r.isCustom && (
                <span
                  title={t('settings.platform.plan.linkedCustomHint', {
                    defaultValue: 'Hand-edited, so preset updates skip it by default.',
                  })}
                  className="text-[10px] px-1.5 py-0.5 rounded themed-bg-tertiary themed-text-muted shrink-0"
                >
                  {t('settings.platform.plan.linkedCustom', { defaultValue: 'Custom' })}
                </span>
              )}
            </div>
          ))}
          {rows.length < total && (
            <button
              onClick={() => void loadMore()}
              className="w-full text-center text-[11px] themed-text-muted hover:themed-text py-1.5"
            >
              {t('settings.platform.plan.linkedMore', {
                defaultValue: 'Show more ({{n}} remaining)', n: total - rows.length,
              })}
            </button>
          )}
        </div>
      )}

      {canEdit && (
        <div className="mt-2">
          {linking ? (
            <LinkTenantPicker
              plan={plan}
              onDone={() => { setLinking(false); void load() }}
              onCancel={() => setLinking(false)}
            />
          ) : (
            <button
              onClick={() => setLinking(true)}
              className="px-2.5 py-1 text-xs rounded themed-btn-secondary"
            >
              {t('settings.platform.plan.linkTenants', { defaultValue: 'Link tenants' })}
            </button>
          )}
        </div>
      )}
    </div>
  )
}

/**
 * The "Link tenants" picker — search the tenant list, link a match to this plan.
 *
 * A tenant is linked with `planAdminApi.set(id, plan.id)` (one POST each): the
 * same audited single-tenant assignment the customer's Plan tab makes. Linked
 * rows drop out of the results immediately so a double-click cannot re-link.
 */
function LinkTenantPicker({
  plan, onDone, onCancel,
}: {
  plan: { id: string; name: string }
  onDone: () => void
  onCancel: () => void
}) {
  const { t } = useTranslation('core-ui')
  const toast = useToast()
  const [q, setQ] = useState('')
  const [results, setResults] = useState<AdminTenantRow[]>([])
  const [searching, setSearching] = useState(false)
  const [linkingId, setLinkingId] = useState<string | null>(null)
  const [linked, setLinked] = useState<Set<string>>(new Set())

  // Debounced search: a query change waits 300ms before hitting the route, so
  // typing "acme" is one request, not four.
  useEffect(() => {
    const term = q.trim()
    if (!term) { setResults([]); return }
    let live = true
    setSearching(true)
    const h = setTimeout(async () => {
      try {
        const res = await budgetApi.listTenants({ search: term, limit: 10 })
        if (live) setResults(res.tenants)
      } catch {
        if (live) setResults([])
      } finally {
        if (live) setSearching(false)
      }
    }, 300)
    return () => { live = false; clearTimeout(h) }
  }, [q])

  const link = async (id: string) => {
    setLinkingId(id)
    try {
      await planAdminApi.set(id, plan.id)
      setLinked(prev => new Set(prev).add(id))
      toast.success(t('settings.platform.plan.linked', { defaultValue: 'Linked to {{plan}}.', plan: plan.name }))
    } catch (e: any) {
      toast.error(e?.message || 'Failed to link the tenant.')
    } finally {
      setLinkingId(null)
    }
  }

  return (
    <div className="themed-bg-primary rounded border themed-border p-2">
      <div className="flex items-center gap-2 mb-2">
        <input
          value={q}
          onChange={e => setQ(e.target.value)}
          autoFocus
          placeholder={t('settings.platform.plan.linkSearch', { defaultValue: 'Search tenants by name or email…' })}
          aria-label={t('settings.platform.plan.linkSearch', { defaultValue: 'Search tenants by name or email…' })}
          className="themed-input border rounded px-2 py-1 text-xs flex-1 outline-none focus:border-blue-500"
        />
        <button onClick={onDone} className="px-2 py-1 text-[11px] rounded themed-btn-secondary">
          {t('common:done', { defaultValue: 'Done' })}
        </button>
        <button onClick={onCancel} className="px-2 py-1 text-[11px] rounded themed-btn-secondary">
          {t('common:cancel')}
        </button>
      </div>
      {searching ? (
        <div className="themed-text-muted text-xs py-1">{t('common:loading')}</div>
      ) : results.length === 0 ? (
        <div className="themed-text-muted text-xs py-1">
          {q.trim()
            ? t('settings.platform.plan.linkNoResults', { defaultValue: 'No tenants match.' })
            : t('settings.platform.plan.linkPrompt', { defaultValue: 'Type to find a tenant.' })}
        </div>
      ) : (
        <div className="max-h-48 overflow-y-auto">
          {results.map(r => {
            const isLinked = linked.has(r.id)
            return (
              <div key={r.id} className="flex items-center gap-2 px-1.5 py-1.5 border-b themed-border last:border-b-0">
                <span className="min-w-0 flex-1">
                  <span className="themed-text text-xs block truncate">{r.name}</span>
                  {/* The email says WHICH tenant: two personal tenants can share a
                      name, and Link puts this one on a plan. */}
                  {(() => {
                    const who = tenantPickerEmail(r, t)
                    return (
                      <span className="themed-text-muted text-[10px] flex items-center gap-1 min-w-0">
                        <span className="truncate min-w-0" title={who?.email}>
                          {r.type}{who && <> · {who.email}</>}
                        </span>
                        {who?.mark && <span className="shrink-0 whitespace-nowrap themed-chip-warn">({who.mark})</span>}
                      </span>
                    )
                  })()}
                </span>
                <button
                  onClick={() => void link(r.id)}
                  disabled={linkingId === r.id || isLinked}
                  className="px-2 py-1 text-[10px] rounded themed-btn-secondary disabled:opacity-50 shrink-0"
                >
                  {isLinked
                    ? t('settings.platform.plan.linkedShort', { defaultValue: 'Linked' })
                    : t('settings.platform.plan.linkAction', { defaultValue: 'Link' })}
                </button>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

/**
 * The email that says WHICH tenant a picker row is, and what it is: a personal
 * account's main email (marked when the account never proved it — a squat,
 * #595 — so it is not read as that person's), else an owner, else an owner
 * still invited. The mark is returned SEPARATELY so the caller renders it
 * outside the truncating email: a long address must not clip the warning away.
 */
function tenantPickerEmail(
  r: { primaryEmail?: string | null; primaryEmailVerified?: boolean | null; ownerEmails?: string[]; invitedOwnerEmails?: string[] },
  t: (key: string, opts: { defaultValue: string }) => string,
): { email: string; mark: string | null } | null {
  if (r.primaryEmail) {
    return {
      email: r.primaryEmail,
      mark: r.primaryEmailVerified === false
        ? t('settings.platform.customers.unverifiedEmail', { defaultValue: 'Unverified' })
        : null,
    }
  }
  if (r.ownerEmails?.[0]) return { email: r.ownerEmails[0], mark: null }
  if (r.invitedOwnerEmails?.[0]) {
    return { email: r.invitedOwnerEmails[0], mark: t('settings.platform.customers.invitedOwnerLabel', { defaultValue: 'Invited owner' }) }
  }
  return null
}
